import { z } from 'zod';

export type Bonus = {
  activationDate: string;
  bonusChannel: number;
  bonusDescription: string;
  expirationDate: string;
  idBonus: number;
  initialAmount: number;
  originBonus: number;
  originBonusId: string;
  promoDescription: string;
  promoDetail: string;
  promoId: number;
  residualAmount: number;
};

export type GameBonus = {
  activationDate: string;
  bonusDescription: string;
  bonusTypology: string;
  expirationDate: string;
  gameBonusDetail: Record<string, any>;
  initialAmount: number;
  promoDescription: string;
};

export type GameBonusDetail = {
  description: string;
  funBonusEnabled: boolean;
  gameProvider: Record<string, string | number>;
  gameSubType: Record<string, string | number>;
  gameType: Record<string, string | number>;
  id: number;
  providerGameCode: string;
  regulatorCode: number;
};
export type PromoCodeRequestPayload = {
  coupon: string;
};

export type Game = {
  description: string;
  gameProvider: { id: number; description: string };
  gameSubType: { id: number; description: string };
  gameType: { id: number; description: string };
};

export type GameBonusServiceResponse = {
  idBonus: number; // ---> ID Del Bonus ( usato per chiamare il servizio di dettaglio )
  id: number;
  bonusType: string; // ---> Tipo bonus ( BW (Bonus Wager) - FUN (Fun bonus) - BS (Real bonus)) (usato per i filtri e logica all'interno dei vari componenti)
  bonusName?: string; // ---> Nome del bonus ( usato nella colonna NOME  )
  promoName?: string; // ---> Nome del bonus Atena ( usato nella colonna NOME  )
  productName: string; // ---> Nome del prodotto ( usato nella colonna PRODOTTO )
  bonusTemplateName: string;
  bonusTemplateDescription?: string;
  validFromDate: string;
  extId: string; // ---> Identificativo del bonus ( usato dal popup DETTAGLI )
  games?: Game[]; // <-- Usa l'array di giochi definiti sopra
  productDescription: string; // ---> Descrizione del prodotto ( usato nella colonna PRODOTTO )
  expiryDate: string; // ---> Scadenza del bonus ( usato nella colonna SCADENZA )
  accreditationDate?: string; // ---> Data accredito del bonus ( usato nella colonna ACCREDITO )
  validFromTimestamp?: number; // ---> Data accredito del bonus ( usato nella colonna ACCREDITO )
  validityStart?: number; // ---> Data accredito del bonus Atena ( usato nella colonna ACCREDITO )
  expirationTimestamp?: number; // ---> Scadenza del bonus ( usato nella colonna SCADENZA )
  validityEnd?: number; // ---> Scadenza del bonus Atena ( usato nella colonna SCADENZA )
  initialAmount: number; // ---> Importo iniziale bonus ( usato nella colonna IMPORTO --> 12.7/initialAmount € )
  betAmount: number;
  totalBetAmount: number;
  totalWinAmount: number;
  balance: number;
  currentAmount: number; // ---> Importo attuale bonus ( diventerà importo convertito ) ( usato nella colonna IMPORTO --> currentAmount/initialAmount € )
  freeSpin?: {
    amount: number;
    betAmount: number;
    remaining: number;
    used: number;
    // For FreespinGold :
    minWin: number;
    minBet: number;
  };
  totWin: number;
  totSpin: number;
  gameProviders?: [{ id: number; description: string }];
  gameSubTypes?: [{ id: number; description: string }];
  spinAmount: number;
  maxBalanceAmount: number; // ---> Massimo importo bonus ( importo convertibile ( dato dal calcolo Wagering * initialAmount (calcolo fatto lato BE ) ) ) ( usato nella colonna STATO BONUS )
  wagering?: number; // ---> Wagering (usato nella colonna WAGERING & modale dettaglio FUN bonus e Bonus Wager)
  idleThreshold?: number; // ---> Wagering (usato nella colonna WAGERING & modale dettaglio FUN bonus e Bonus Wager)
  expired?: boolean; // ---> Stato del bonus ( scaduto/attivo ) (usato principalmente stile riga & logica modale dettaglio apparizione bannerino conferma vincita o non vincita del bonus )
  rolloverAmount?: number; // ---> ??
  progressBarStatus?: number; // --> Calcolo percentuale barra nella colonna STATO BONUS fatto lato BE ( usata per mostrare percentuale nella barra STATO BONUS, viene NULL quando è REAL bonus ( bonustyp === "BS" ) per far sparire la barra )
  bonusState?: string; // ---> usato per logica modale dettaglio ( bannerino ) FUN bonus (CONVERTED - EXPIRED - IDLE) e Bonus Wager (CREATED - PENDING - ACTIVE - CONVERTED - EXPIRED - SUSPENDED - CANCELED - TO BE ACCEPTED - TO BE CONVERTED)
  status?: { id: number; description: string };
  maxWinAmount: number;
  convertedAmount: string;
  conversionLimit: number; // ---> Limite di conversione usato nella modale dettaglio Bonus Wager
  xConversion: number;
};

export type GameBonusWagerDetail = GameBonusServiceResponse & {
  idBonus: number;
  bonusType: string;
  initialAmount: number;
  currentAmount: number;
  bonusState: string;
  productAttributes?: [
    {
      productId: number;
      attributes: [
        {
          code: string;
          description: string;
          value: string;
          operator: string;
        },
      ];
    },
  ];
  bonusTemplateName: string;
  bonusTemplateDescription: string;
};

export type FreeSpin = {
  amount: number;
  betAmount: number;
  used: number;
  remaining: number;
};

export type BonusStatus = {
  id: number;
  description: string;
};

export type BonusType = {
  id: number;
  description: string;
};

export type GameBonusFunDetail = GameBonusServiceResponse & {
  balance: number;
  betAmount: number;
  convertedAmount: string;
  extId: string;
  freeSpin: FreeSpin;
  games: Game[];
  id: number;
  idBonus: number;
  idleThreshold: number;
  initialAmount: number;
  maxWinAmount: number;
  playerAccountCode: string;
  priority: number;
  promoId: number;
  promoName: string;
  status: BonusStatus;
  totalBetAmount: number;
  totalWinAmount: number;
  type: BonusType;
  bonusType: string;
  validityEnd: number;
  validityStart: number;
  progressBarStatus: number;
  expired?: boolean;
};

const bonusSpendabilitySchema = z.object({
  source: z.string(),
  bonusTypeCode: z.string(),
  bonusTypeDescription: z.string(),
  originalAmount: z.number(),
  residualAmount: z.number(),
  products: z
    .array(
      z.object({
        code: z.string(),
        description: z.string(),
        originalAmount: z.number(),
        residualAmount: z.number(),
      }),
    )
    .optional(),
});
export type BonusSpendability = z.infer<typeof bonusSpendabilitySchema>;

export const getBonusSpendabilityResponseSchema = z.object({
  status: z.string(),
  requestId: z.string(),
  datetime: z.string(),
  idConto: z.string(),
  bonuses: z.array(bonusSpendabilitySchema),
  correlationId: z.string(),
});
export type GetBonusSpendabilityResponse = z.infer<
  typeof getBonusSpendabilityResponseSchema
>;
